import React, { useState } from 'react';
import { CalendarDays, Euro, FileText, Hash, List, Plus, Search, Undo2 } from 'lucide-react';
import { DataTable, ColumnDef } from '../../../../../components/shared/DataTable/DataTable';
import { MasterDetailLayout } from '../../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, viewAction } from '../../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { DetailPanel } from '../../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailCard,
  DetailField,
  DetailFieldList,
  DetailIdentity,
  DetailNotes
} from '../../../../../components/shared/DetailPanel/DetailBlocks';
import { useClickOutside, isDetailKeepZone } from '../../../../../hooks/useClickOutside';
import { nextInvoiceNumber } from '../../../../../services/InvoicesService';
import CreditNoteForm from '../forms/CreditNoteForm';
import { formatDate } from '../forms/FormParts';
import { CreditNote, PRESET_CREDIT_NOTES, STORAGE_KEYS, formatEuro } from './financeMasters';
import { useStoredList, writeStoredList } from './useStoredList';
import '../../shared-modules.css';
import './FinanceMasters.css';

const SERIES = 'NC-2026';

interface FinanceCreditNotesViewProps {
  privacyMode: boolean;
  onNavigate: (groupId: string, subScreenId?: string) => void;
  // Abre directamente el formulario de una nota nueva
  startNew?: boolean;
}

// Las notas emitidas no se editan: ya tienen su asiento
export const FinanceCreditNotesView = ({ privacyMode, onNavigate, startNew = false }: FinanceCreditNotesViewProps) => {
  const [notes, setNotes] = useStoredList<CreditNote>(STORAGE_KEYS.creditNotes, PRESET_CREDIT_NOTES);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNote, setSelectedNote] = useState<CreditNote | null>(null);

  const closePanel = () => setSelectedNote(null);

  useClickOutside(isDetailKeepZone, closePanel, Boolean(selectedNote));

  const handleSave = (note: CreditNote) => {
    const next = [note, ...notes];
    setNotes(next);
    writeStoredList(STORAGE_KEYS.creditNotes, next);
  };

  if (startNew) {
    const creditedByInvoice = notes.reduce<Record<string, number>>((acc, n) => {
      if (n.invoiceId) acc[n.invoiceId] = (acc[n.invoiceId] || 0) + n.total;
      return acc;
    }, {});
    return (
      <CreditNoteForm
        privacyMode={privacyMode}
        nextNumber={nextInvoiceNumber(notes, `${SERIES}-`)}
        creditedByInvoice={creditedByInvoice}
        onSave={handleSave}
        onClose={() => onNavigate('deudores', 'notas_credito')}
      />
    );
  }

  const query = searchQuery.toLowerCase();
  const filteredNotes = notes.filter(n =>
    n.number.toLowerCase().includes(query) ||
    n.clientName.toLowerCase().includes(query) ||
    n.invoiceNumber.toLowerCase().includes(query) ||
    n.reason.toLowerCase().includes(query)
  );

  const columns: ColumnDef<CreditNote>[] = [
    {
      id: 'number',
      header: 'Nº NOTA',
      render: (n) => <span className="fin-master__code">{n.number}</span>
    },
    {
      id: 'client',
      header: 'CLIENTE',
      render: (n) => (
        <div>
          <div className="fin-master__name">{n.clientName}</div>
          {n.clientCif && <div className="fin-master__sub">CIF: {n.clientCif}</div>}
        </div>
      )
    },
    {
      id: 'invoice',
      header: 'FACTURA ORIGEN',
      render: (n) => <span className="fin-master__text">{n.invoiceNumber}</span>
    },
    {
      id: 'date',
      header: 'FECHA',
      render: (n) => <span className="fin-master__text">{formatDate(n.date)}</span>
    },
    {
      id: 'reason',
      header: 'MOTIVO',
      render: (n) => <span className="fin-master__desc">{n.reason}</span>
    },
    {
      id: 'total',
      header: 'TOTAL ABONO',
      align: 'right',
      render: (n) => <span className="fin-master__amount">-{formatEuro(n.total, privacyMode)}</span>
    }
  ];

  return (
    <div className="fin-master">
      <div className="module-toolbar">
        <div className="module-search-wrapper">
          <Search className="module-search-icon" size={14} />
          <input
            type="text"
            placeholder="BUSCAR POR Nº, CLIENTE, FACTURA O MOTIVO..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="module-search-input"
          />
        </div>
        <button onClick={() => onNavigate('deudores', 'crear_nota_credito')} className="module-create-btn">
          <Plus size={14} /> Nueva_Nota_De_Crédito
        </button>
      </div>

      <MasterDetailLayout isOpen={Boolean(selectedNote)}>
        <DataTable
          data={filteredNotes}
          columns={columns}
          title={<><Undo2 size={14} className="module-panel-title-icon" /> Notas_De_Crédito_Emitidas</>}
          subtitle={`TOTAL: ${filteredNotes.length}`}
          getRowId={(n) => n.number}
          onRowClick={(n) => setSelectedNote(n)}
          selectedRowId={selectedNote?.number}
          emptyText="No se encontraron notas de crédito."
          actionsHeader="ACCIONES"
          actionsColumn={(n) => <RowActionsMenu actions={[viewAction(() => setSelectedNote(n))]} />}
        />

        {selectedNote && (
          <DetailPanel
            expandable
            title="Ficha_De_Nota_De_Crédito"
            onClose={closePanel}
            cards={
              <DetailCard icon={List} title={`Líneas (${selectedNote.lines.length})`}>
                <div className="fin-master__attachments">
                  {selectedNote.lines.map(l => (
                    <div key={l.id} className="fin-master__attachment">
                      <span>{l.description}</span>
                      <span className="fin-master__sub">
                        {l.quantity} × {formatEuro(l.price, privacyMode)} • IVA {l.vat}%
                      </span>
                    </div>
                  ))}
                </div>
              </DetailCard>
            }
          >
            <DetailIdentity
              icon={Undo2}
              reference={selectedNote.number}
              name={selectedNote.clientName}
              subtitle={`Sobre la factura ${selectedNote.invoiceNumber}`}
            />

            <DetailFieldList>
              <DetailField icon={Hash} label="CIF">{selectedNote.clientCif || '—'}</DetailField>
              <DetailField icon={FileText} label="FACTURA_ORIGEN">{selectedNote.invoiceNumber}</DetailField>
              <DetailField icon={CalendarDays} label="FECHA">{formatDate(selectedNote.date)}</DetailField>
              <DetailField icon={Euro} label="SUBTOTAL">{formatEuro(selectedNote.subtotal, privacyMode)}</DetailField>
              <DetailField icon={Euro} label="IVA">{formatEuro(selectedNote.vat, privacyMode)}</DetailField>
              <DetailField icon={Euro} label="TOTAL_ABONO">{formatEuro(selectedNote.total, privacyMode)}</DetailField>
            </DetailFieldList>

            <DetailNotes title="Motivo">{selectedNote.reason}</DetailNotes>
          </DetailPanel>
        )}
      </MasterDetailLayout>
    </div>
  );
};

export default FinanceCreditNotesView;
